import {
  Gender,
  IDeliveryInformationConfig,
  IPatient,
  IPostalCode,
  ISingleOrder,
  Therapy,
  TherapyType,
} from '@alberta/konexi-shared';
import { IPostalCodeInfoConfig } from '@alberta/konexi-shared/dist/general/config/postal-code-info-config';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { AbstractControl, FormControl, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { SingleOrderDB } from '@common/repository/databases';
import { SQLiteQueryBuilder } from '@common/storage/sqlite-query-builder';
import { AlertController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { QueryService } from '@services/query/query.service';
import { cloneDeep } from 'lodash';
import { Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import { DeliveryAddressViewModel } from 'src/app/business/order/delivery-address-viewmodel';
import { OrderDeliveryInformationService } from 'src/app/business/order/order-delivery-information-service';
import { PatientComponentModel } from 'src/app/business/patient/patients-component-model';
import { getSelectionItems } from 'src/app/business/selectionitem-factory';
import { addSingleOrderAddressControls } from 'src/app/business/single-order/single-order-formbuilder';
import { SingleOrderViewModel } from 'src/app/business/single-order/single-order-view-model';
import { dateTodayOrInFutureValidator } from 'src/app/common/date/date-validators';
import { getDeliveryEndDate } from 'src/app/common/date/order-dates';
import { compareDeliveryAddresses } from 'src/app/shared/comparator/address-comparator';
import { arraysEqual } from 'src/app/shared/comparator/array-comparer';
import { AutocompleteComponent } from 'src/app/shared/components/inputs/autocomplete/autocomplete.component';
import { DateType } from 'src/app/shared/components/inputs/date-time/date-time.component';
import { PostalCodeModelName } from 'src/app/shared/models/model-names';
import { SelectionItem } from 'src/app/shared/models/selectionItem';
import { DeliveryAddressesService } from 'src/app/shared/services/delivery-addresses.service';
import { GeneralEntityService } from 'src/app/shared/services/general-entity.service';
import { PatientService } from 'src/app/shared/services/patient.service';
import { TenantConfigService } from 'src/app/shared/services/tenant-config/tenant-config.service';
import { TitlesService } from 'src/app/shared/services/tenant-config/titles.service';

@Component({
  selector: 'itl-single-order-masterdata',
  templateUrl: './single-order-masterdata.component.html',
  styleUrls: ['./single-order-masterdata.component.scss'],
  providers: [OrderDeliveryInformationService],
})
export class SingleOrderMasterdataComponent implements OnInit, OnDestroy {
  @Input()
  public order: SingleOrderViewModel;
  @Input()
  public isNew: boolean;
  @Input()
  public hasReminder: boolean;
  @Input()
  public form: UntypedFormGroup;
  @Input()
  public reminderForm: UntypedFormGroup;
  @Output()
  public reminderToggled: EventEmitter<boolean> = new EventEmitter<boolean>();

  public loading = true;
  private deliveryPostalCodeAutoComplete: AutocompleteComponent;

  @ViewChild('deliveryAddressPostalCodeIdAutocomplete') set deliveryAddressPostalCodeIdAutocomplete(
    content: AutocompleteComponent
  ) {
    if (content) {
      this.deliveryPostalCodeAutoComplete = content;
      this.subscription.add(
        this.deliveryPostalCodeAutoComplete.selectedChange.subscribe(async postalCodeId => {
          this._itemsProvider.type = PostalCodeModelName;
          const postalCode: IPostalCode = await this._itemsProvider.find(postalCodeId);
          this.order.deliveryAddress.postalCodeId = postalCode?._id;
          this.order.deliveryAddress.city = postalCode?.city;
          this.order.deliveryAddress.postalCode = postalCode?.postalCode;
          this.form.controls['deliveryAddressPostalCodeId'].updateValueAndValidity();
        })
      );
    }
    this.updateRelevantPostalCodeInfoText();
  }
  public minDate = new Date();
  public startTime: Date;
  public dateType = DateType.Time;
  public accountingMonthNames: SelectionItem[] = [];
  public orderTagsInConfig: boolean = false;
  public orderTags: SelectionItem[] = [];
  public tags: AbstractControl<string | string[]> = new FormControl();
  public deliveryAddresses: DeliveryAddressViewModel[] = [];
  public predefinedDeliveryAddresses: DeliveryAddressViewModel[] = [];
  public adressesSelectionItems: SelectionItem[] = [];
  public deliveryDate: AbstractControl;
  public startDate: AbstractControl;
  public endDate: AbstractControl;
  public accountingMonth: AbstractControl;
  public deliveryAddress: AbstractControl;
  public deliveryInformation: AbstractControl;
  public deliveryStartTime: AbstractControl;
  public deliveryNote: AbstractControl;
  public reminderDate: AbstractControl;
  public reminderText: AbstractControl;
  public genderValues: SelectionItem[] = [];
  public titleValues: SelectionItem[] = [];
  public deliveryInformationSelectionItems: SelectionItem[] = [];
  public deliveryInformationRequired: boolean;
  @Input()
  public canSetDeliveryDateUnknown: boolean;
  @Input()
  public isMonthlyOrderActivated: boolean;
  public showAddressDetails = false;
  public showTimeSelection = false;
  public hasDeliveryNote = false;
  public deliveryNoteMaxLength: number;
  public showOrderPrescriberToggle = false;
  private postalCodeInfos: IPostalCodeInfoConfig[];
  public postalCodeInfoText: string;
  public orderTagsSelectStyle: 'single' | 'multiple';
  public orderTagsRequired: boolean;
  private _lastEvaluatedAccountingMonth: number[];
  private subscription = new Subscription();
  private deliveryInformationOptions: IDeliveryInformationConfig[];
  private _patient: IPatient;
  private readonly _AOKTag = 'AOK-Dienstleistungspauschale';
  private readonly _AOKID = '5ada01a2c3918e1bdcb54611';
  private _useAOKTagPrefillWorkflow: boolean = false;

  public deliveryOptionRequired: boolean;
  public deliveryOptionSelectionItems: SelectionItem[] = [];
  public showDeliveryOptions = false;
  public deliveryOption: AbstractControl;

  constructor(
    private _patientComponentModel: PatientComponentModel,
    public translate: TranslateService,
    private _deliveryAdressesProvider: DeliveryAddressesService,
    private _patientProvider: PatientService,
    private _alertCtrl: AlertController,
    private _itemsProvider: GeneralEntityService,
    titleService: TitlesService,
    private _tenantConfig: TenantConfigService,
    private _orderDeliveryInformationService: OrderDeliveryInformationService,
    private _deliveryAdressesService: DeliveryAddressesService,
    private _formBuilder: UntypedFormBuilder,
    private _queryService: QueryService
  ) {
    this.genderValues = getSelectionItems(Gender, 'patient.details.gender.', this.translate);

    // tslint:disable-next-line: no-floating-promises
    titleService.getSortedTitleSelectionItems().then(titles => (this.titleValues = titles));
  }

  async ngOnInit(): Promise<void> {
    const tenantConfig = await this._tenantConfig.getTenantConfig();
    this.orderTagsSelectStyle = await this._tenantConfig.orderTagsSelectStyle();
    this.createControls();
    this._patient = await this.loadPatient();
    this.getPatientDeliveryInformation(this._patient);
    const integratedCareId = this.order.integratedCareId;
    this.startTime = this.order.getDeliveryStartDate();
    this.deliveryInformationRequired = await this._tenantConfig.deliveryInformationRequired();
    this.hasDeliveryNote = await this._tenantConfig.hasDeliveryNote();
    this._useAOKTagPrefillWorkflow = await this._tenantConfig.useAOKTagPrefillWorkflow();
    this.deliveryNoteMaxLength = await this._tenantConfig.getDeliveryNoteMaxLength();
    this.deliveryInformationOptions = await this._tenantConfig.getDeliveryInformation();
    this.showOrderPrescriberToggle = await this._tenantConfig.showHospitalPrescriberToggle();
    this.orderTagsRequired = await this._tenantConfig.orderTagsRequired();
    this.showDeliveryOptions = tenantConfig.orderConfig?.deliveryOption?.show;
    this.deliveryOptionRequired = tenantConfig.orderConfig?.deliveryOption?.required;

    const orderTagsFromConfig = await this._tenantConfig.getOrderTags();
    if (orderTagsFromConfig?.length > 0) {
      this.orderTagsInConfig = true;
      this.orderTags = orderTagsFromConfig.map(tag => new SelectionItem(tag, tag));
    }
    // tslint:disable-next-line: max-line-length
    this.deliveryInformationSelectionItems =
      this._orderDeliveryInformationService.createDeliveryInformationSelectionItems(this.deliveryInformationOptions);

    if (this.order.deliveryInformation != null) {
      const selectedDeliveryInformation = this._orderDeliveryInformationService.getSelectedDeliveryInformationOption(
        this.order.deliveryInformation,
        this.deliveryInformationOptions
      );
      if (selectedDeliveryInformation?.isTimeRelevant) {
        this.showTimeSelection = true;
      } else {
        this.showTimeSelection = false;
      }

      if (this.showDeliveryOptions) {
        this.deliveryOptionSelectionItems =
          await this._orderDeliveryInformationService.createDeliveryOptionSelectionItems();
      }
    }
    // tslint:disable-next-line: no-floating-promises
    await this._deliveryAdressesProvider
      .getDeliveryAddressesfromPatient(this._patient, true, integratedCareId, true)
      .then(async values => {
        if (values.length === 0) {
          return;
        }
        this.deliveryAddresses = values;

        if (this._deliveryAdressesProvider.checkDeliveryAddressChanged(this.order.deliveryAddress, values)) {
          const differentOrderAdress = new DeliveryAddressViewModel();

          for (const key of Object.keys(values[0])) {
            differentOrderAdress[key] = this.order.deliveryAddress[key];
          }

          if (differentOrderAdress.type === null || differentOrderAdress.type === undefined) {
            differentOrderAdress.type = values[0].type;
          }
          this.deliveryAddresses.unshift(differentOrderAdress);
        }

        this.predefinedDeliveryAddresses = cloneDeep(this.deliveryAddresses);

        await this.createAddressesSelectionItems();

        if (this.deliveryAddresses.length >= 1 && !this.order.deliveryAddress) {
          this.order.deliveryAddress = this.isNew
            ? { ...this.adressesSelectionItems[0].id }
            : (this.order.deliveryAddress = {
                ...this.adressesSelectionItems[0].id,
                ...this.order.deliveryAddress,
              });
          this.deliveryAddress.setValue(this.deliveryAddresses[0]);
        }

        if (this.isNew && !this.deliveryInformation.value) {
          await this.setDeliveryInformation(this._patient);
        }
        const allPostalCodeInfos = await this._tenantConfig.getPostalCodeInfo();
        if (allPostalCodeInfos?.length) {
          this.postalCodeInfos = allPostalCodeInfos.filter(info => info.entityType === 'singleOrder');
        }
        this.updateRelevantPostalCodeInfoText();
        this.setAccountingMonths();
        await this.prefillOrderTags();

        addSingleOrderAddressControls(this.form, this.order, this._formBuilder);
      });

    this.subscription.add(
      this.tags.valueChanges.subscribe(value => {
        this.order.tags = this.orderTagsSelectStyle === 'single' ? [value as string] : (value as string[]);
        this.form.controls['tags'].setValue(this.order.tags);
      })
    );
    this.loading = false;
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  private deleteAOKTag() {
    if (this.order.tags?.includes(this._AOKTag)) {
      this.order.tags = this.order.tags.filter(tag => tag !== this._AOKTag);
    }
  }

  async accountingMonthChanged(newValue: number[]) {
    if (!arraysEqual(this._lastEvaluatedAccountingMonth, newValue)) {
      await this.prefillOrderTags();
    }
  }
  /**
   * automatic tag prefilling workflow for MWM
   */
  async prefillOrderTags() {
    this._lastEvaluatedAccountingMonth = this.order.accountingMonth;
    if (this.orderTagsInConfig && this._useAOKTagPrefillWorkflow) {
      this.loading = true;
      try {
        const isWoundCare = this.order.therapyId === Therapy.WV;
        const isUserAOKPayer = this._patient?.payer?.payerId === this._AOKID;
        // feature only works, if only one accounting month is set at the order
        const isAccountingMonthValid = this.order.accountingMonth?.length === 1;
        if (!isUserAOKPayer || !isAccountingMonthValid || !isWoundCare) {
          this.loading = false;
          return;
        }
        let currentAccountingMonth = this.order.accountingMonth[0];
        const selectedTherapyType = TherapyType[this.order.therapyTypeId];
        let patientOrdersWithCurrentTherapyType = await this._queryService.raw<ISingleOrder>(
          {
            sql: new SQLiteQueryBuilder()
              .selectAll()
              .fromFts(SingleOrderDB)
              .where('patientId', '=', `${this.order.patientId}`)
              .and('therapyTypeId', '=', `${this.order.therapyTypeId}`)
              .innerJoin(SingleOrderDB, 'id', '=', 'key')
              .asString(),
            aggregation: [
              {
                $match: {
                  patientId: this.order.patientId,
                  archived: { $ne: true },
                  therapyTypeId: selectedTherapyType,
                },
              },
              {
                $project: {
                  _id: 1,
                  tags: 1,
                  accountingMonth: 1,
                },
              },
            ],
          },
          SingleOrderDB
        );
        if (patientOrdersWithCurrentTherapyType.length > 0) {
          const ordersWithTag = patientOrdersWithCurrentTherapyType.filter(order => order.tags?.includes(this._AOKTag));
          // dont set tag if already set 3 times overall
          if (ordersWithTag.length >= 3) {
            this.deleteAOKTag();
            this.loading = false;
            return;
          }
          if (0 <= ordersWithTag.length && ordersWithTag.length < 3) {
            // check if the tag is already set with current month
            const ordersWithTagInCurrentMonth = ordersWithTag.filter(order =>
              order.accountingMonth?.includes(currentAccountingMonth)
            );
            if (ordersWithTagInCurrentMonth.length >= 1) {
              this.deleteAOKTag();
              this.loading = false;
              return;
            }
          }
        }
        // set the tag if no condition is met and not set yet
        if (!this.order.tags) {
          this.order.tags = [this._AOKTag];
          this.loading = false;
          return;
        }

        if (!this.order.tags?.includes(this._AOKTag)) {
          this.order.tags.push(this._AOKTag);
        }
      } catch (e) {
        window.logger.error('Error prefilling Tags', e);
      }
      this.loading = false;
    }
  }

  public async deliveryDateUnknownToggled($event) {
    if ($event) {
      this.order.setDeliveryTimeFrame();
      this.startDate.setValue(this.order.startDate);
      this.endDate.setValue(this.order.endDate);
      this.setAccountingMonths();
    }

    const isFalse = typeof $event === 'boolean' && !$event;
    const isTrue = typeof $event === 'boolean' && $event;

    // reset deliveryDate to empty if it is initially empty
    if (isFalse && !(await this._tenantConfig.mustSetDeliveryDate())) {
      this.order.deliveryDate = null;
      this.deliveryDate.setValue(this.order.deliveryDate);
    }

    // remove required validator if delivery date is set to unknown
    if (isTrue && (await this._tenantConfig.canSetDeliveryDateUnknown())) {
      this.deliveryDate.clearValidators();
      this.startDate.clearValidators();
      this.endDate.clearValidators();
    } else {
      const deliveryDateValidators = [Validators.required, dateTodayOrInFutureValidator];
      this.deliveryDate.setValidators(deliveryDateValidators);
      this.startDate.setValidators([Validators.required]);
      this.endDate.setValidators(deliveryDateValidators);
    }
    this.deliveryDate.updateValueAndValidity();

    this.order.deliveryDateUnknown = isFalse ? false : $event;
  }

  public resetStartTime(value: string) {
    // tslint:disable-next-line: max-line-length
    const selectedDeliveryInformationOption =
      this._orderDeliveryInformationService.getSelectedDeliveryInformationOption(
        value,
        this.deliveryInformationOptions
      );
    if (selectedDeliveryInformationOption != null && selectedDeliveryInformationOption.isTimeRelevant) {
      this.showTimeSelection = true;
    } else {
      this.showTimeSelection = false;
      this.form.get('deliveryStartTime').setErrors(null);
      this.form.controls['deliveryInformation'].setValue(value);
      if (this.order.deliveryStartTime != null) {
        this.order.deliveryStartTime = null;
        this.startTime = null;
      }
    }
  }

  public changeStartTime(value: Date) {
    this.order.setDeliveryStartTime(value);
    this.startTime = value;
  }

  public isHospitalPrescriberChanged(event: boolean) {
    if (event && this._patient.hospital?.hospitalId) {
      this.order.hospitalId = this._patient.hospital.hospitalId;
    } else {
      this.order.hospitalId = null;
    }
  }

  private createControls(): void {
    this.deliveryDate = this.form.controls['deliveryDate'];
    this.startDate = this.form.controls['startDate'];
    this.endDate = this.form.controls['endDate'];
    this.accountingMonth = this.form.controls['accountingMonth'];
    this.deliveryAddress = this.form.controls['deliveryAddress'];
    this.deliveryInformation = this.form.controls['deliveryInformation'];
    this.deliveryStartTime = this.form.controls['deliveryStartTime'];
    this.deliveryOption = this.form.controls['deliveryOption'];
    this.deliveryNote = this.form.controls['deliveryNote'];
    this.reminderDate = this.reminderForm.controls['date'];
    this.reminderText = this.reminderForm.controls['annotation'];
    this.tags.setValue(
      this.orderTagsSelectStyle === 'single' ? this.form.controls['tags'].value?.[0] : this.form.controls['tags'].value
    );

    if (!this.isNew) {
      this.deliveryDate.markAsTouched();
      this.startDate.markAsTouched();
      this.endDate.markAsTouched();
    }
  }

  private async createAddressesSelectionItems(): Promise<void> {
    this.deliveryAddresses.forEach(item => this.addItemToAddressesSelectionItems(item));
    const userAddress = await this._deliveryAdressesService.getUserAddress();
    if (userAddress && !this.deliveryAddresses.includes(userAddress)) {
      this.addItemToAddressesSelectionItems(userAddress);
    }
  }

  private addItemToAddressesSelectionItems(item) {
    const additionalAddress = [item.additionalAddress, item.additionalAddress2].join(' ').trim();
    this.adressesSelectionItems.push(
      new SelectionItem(
        item,
        `${item.type}: ${item.name} \n${additionalAddress} \n${item.address} \n${item.postalCode} ${item.city}`
      )
    );
  }

  private async setDeliveryInformation(patient: IPatient): Promise<void> {
    if (patient?.deliveryInformation?.deliveryInformation) {
      this.order.deliveryInformation = patient.deliveryInformation.deliveryInformation;
      this.deliveryInformation.setValue(this.order.deliveryInformation);
      return;
    }
    if (this.deliveryInformationSelectionItems.length) {
      this.order.deliveryInformation = this.deliveryInformationSelectionItems[0].id;
      this.deliveryInformation.setValue(this.order.deliveryInformation);
    }
  }

  // Load Patient form State or from Store
  private async loadPatient(): Promise<IPatient> {
    if (this.order.patientId) {
      return this._patientProvider.find(this.order.patientId);
    }
    const item$ = this._patientComponentModel.select<IPatient>('selectedItem');
    return new Promise<IPatient>(resolve => {
      item$.pipe(take(1)).subscribe(patient => resolve(patient));
    });
  }

  public async deliveryDateChanged() {
    // The dialogue should be hidden when monthly order is activated
    if (!this.isMonthlyOrderActivated) {
      await this.showUpdateScopeDialog();
    }
  }

  public scopeChanged() {
    this.setAccountingMonths();
    this.order.initMaxAmount(this.order.integratedCare, this._queryService);
  }

  public compareAddresses(address1: DeliveryAddressViewModel, address2: DeliveryAddressViewModel): boolean {
    return compareDeliveryAddresses(address1, address2);
  }

  private async showUpdateScopeDialog() {
    const alert = await this._alertCtrl.create({
      header: this.translate.instant('order.edit.updateScope'),
      backdropDismiss: false,
      message: this.translate.instant('order.edit.updateScopeText'),
      buttons: [
        {
          text: this.translate.instant('NO'),
        },
        {
          text: this.translate.instant('YES'),
          handler: () => {
            this.order.startDate = this.order.deliveryDate;
            this.order.endDate = getDeliveryEndDate(this.order.startDate, this.order.therapyId);
            this.startDate.setValue(this.order.startDate);
            this.endDate.setValue(this.order.endDate);
            this.setAccountingMonths();
          },
        },
      ],
    });
    await alert.present();
  }
  private setAccountingMonths() {
    this.calcMonths();
    this.correctSelectedMonths();
    if (!this.order.accountingMonth || !this.order.accountingMonth.length) {
      this.preSelectFirstMonth();
    }
  }
  private calcMonths(): void {
    if (!this.order.startDate || !this.order.endDate) {
      return;
    }

    const accountingMonthSelectionItems = [];
    const startMonth = this.order.startDate.getMonth();
    const endMonth = this.order.endDate.getMonth();

    if (startMonth <= endMonth) {
      for (let i = startMonth; i <= endMonth; i++) {
        accountingMonthSelectionItems.push(new SelectionItem(i, this.translate.instant(`months.${i}`)));
      }
    } else {
      for (let i = startMonth; i < 12; i++) {
        accountingMonthSelectionItems.push(new SelectionItem(i, this.translate.instant(`months.${i}`)));
      }
      for (let i = 0; i <= endMonth; i++) {
        accountingMonthSelectionItems.push(new SelectionItem(i, this.translate.instant(`months.${i}`)));
      }
    }
    this.accountingMonthNames = this.removeDuplicates(accountingMonthSelectionItems);
  }
  private preSelectFirstMonth(): void {
    this.order.accountingMonth = [];
    if (this.accountingMonthNames[0]) {
      this.order.accountingMonth.push(this.accountingMonthNames[0].id);
      this.accountingMonth.setValue(this.order.accountingMonth);
    }
  }
  private removeDuplicates(duplicates: SelectionItem[]): any[] {
    const unique_array = [];
    for (const item of duplicates) {
      if (unique_array.indexOf(item) === -1) {
        unique_array.push(item);
      }
    }
    return unique_array;
  }

  private correctSelectedMonths() {
    if (!this.order.accountingMonth || !this.order.accountingMonth.length) {
      return;
    }
    const correctMonths = [];
    this.order.accountingMonth.forEach(id => {
      this.accountingMonthNames.find(value => {
        if (value.id === id && !correctMonths.includes(id)) {
          correctMonths.push(id);
        }
      });
    });
    this.order.accountingMonth = correctMonths;
  }

  private getPatientDeliveryInformation(patient: IPatient) {
    if (!this.order.deliveryInformation && patient.deliveryInformation?.deliveryInformation) {
      this.order.deliveryInformation = patient.deliveryInformation.deliveryInformation;
    }
    if (!this.order.deliveryStartTime && patient.deliveryInformation?.startTime) {
      this.showTimeSelection = true;
      this.order.deliveryStartTime = patient.deliveryInformation.startTime;
      this.startTime = this.order.getDeliveryStartDate();
    }
    if (!this.order.deliveryNote && patient.deliveryInformation?.deliveryNote) {
      this.order.deliveryNote = patient.deliveryInformation.deliveryNote;
    }
    if (!this.order.deliveryOption && patient.deliveryInformation?.deliveryOption) {
      this.order.deliveryOption = patient.deliveryInformation.deliveryOption;
    }
  }

  public handleDeliveryAddressChange() {
    this.form.controls['deliveryAddressPostalCodeId']?.setValue(this.order.deliveryAddress.postalCodeId);
    this.form.controls['deliveryAddressName']?.setValue(this.order.deliveryAddress.name);
    this.form.controls['deliveryAddressAddress']?.setValue(this.order.deliveryAddress.address);
    this.updateRelevantPostalCodeInfoText();
  }

  private updateRelevantPostalCodeInfoText() {
    let message = '';
    if (!this.order?.deliveryAddress?.postalCode || this.postalCodeInfos?.length === 0) {
      return;
    }
    const postalCode = Number(this.deliveryAddress?.value?.postalCode || this.order?.deliveryAddress?.postalCode);
    this.postalCodeInfos?.forEach(postalCodeInfo => {
      const from = Number(postalCodeInfo.postalCodeFrom);
      const to = Number(postalCodeInfo.postalCodeTo);
      if (from <= postalCode && postalCode <= to) {
        message += message.length === 0 ? postalCodeInfo.text : '\n' + postalCodeInfo.text;
      }
    });
    this.postalCodeInfoText = message;
  }
}
